import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import moment from "moment";
import type React from "react";
import { useEffect, useState } from "react";
import { getBlockHistory } from "../../apis/blocking/blocking-apis";
import { BLOCK_REASONS, type BlockAuditEntry } from "../../domain/blocking";
import { strings } from "../../I18n";

interface BlockHistoryModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
}

const BlockHistoryModal: React.FC<BlockHistoryModalProps> = ({
  open,
  onClose,
  userId,
}) => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<BlockAuditEntry[]>([]);

  useEffect(() => {
    if (open && userId) {
      loadHistory();
    }
  }, [open, userId]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const data = await getBlockHistory(userId);
      setEntries(data);
    } catch (error) {
      console.error("Failed to load block history:", error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: BlockAuditEntry["action"]) => {
    switch (action) {
      case "BLOCK":
        return <BlockIcon />;
      case "UNBLOCK":
        return <CheckCircleIcon />;
      default:
        return <EditIcon />;
    }
  };

  const getActionColor = (
    action: BlockAuditEntry["action"]
  ): "error" | "success" | "grey" => {
    switch (action) {
      case "BLOCK":
        return "error";
      case "UNBLOCK":
        return "success";
      default:
        return "grey";
    }
  };

  const getActionLabel = (action: BlockAuditEntry["action"]) => {
    switch (action) {
      case "BLOCK":
        return strings("blocking.actionBlocked");
      case "UNBLOCK":
        return strings("blocking.actionUnblocked");
      case "UPDATE_MESSAGE":
        return strings("blocking.actionMessageUpdated");
      case "UPDATE_NOTES":
        return strings("blocking.actionNotesUpdated");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth={true}>
      <DialogTitle>{strings("blocking.blockHistory")}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : entries.length === 0 ? (
          <Typography color="textSecondary" sx={{ py: 4, textAlign: "center" }}>
            {strings("blocking.noHistory")}
          </Typography>
        ) : (
          <Timeline position="right">
            {entries.map((entry, index) => {
              const reasonLabel = entry.blockReason
                ? BLOCK_REASONS.find((r) => r.value === entry.blockReason)
                    ?.label
                : undefined;

              return (
                <TimelineItem key={entry.id}>
                  <TimelineOppositeContent sx={{ maxWidth: 150 }}>
                    <Typography variant="caption" color="textSecondary">
                      {moment(entry.performedAt).format("DD.MM.YYYY")}
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="textSecondary"
                    >
                      {moment(entry.performedAt).format("HH:mm")}
                    </Typography>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color={getActionColor(entry.action)}>
                      {getActionIcon(entry.action)}
                    </TimelineDot>
                    {index < entries.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">
                        {getActionLabel(entry.action)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {strings("blocking.by")} {entry.performedBy}
                      </Typography>
                    </Box>

                    {reasonLabel && (
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={reasonLabel}
                          size="small"
                          color="default"
                        />
                      </Box>
                    )}

                    {entry.blockMessage && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          {strings("blocking.message")}:
                        </Typography>
                        <Typography variant="body2">
                          {entry.blockMessage}
                        </Typography>
                      </Box>
                    )}

                    {entry.internalNotes && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          {strings("blocking.notes")}:
                        </Typography>
                        <Typography variant="body2">
                          {entry.internalNotes}
                        </Typography>
                      </Box>
                    )}

                    {entry.resolutionNotes && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          {strings("blocking.resolution")}:
                        </Typography>
                        <Typography variant="body2">
                          {entry.resolutionNotes}
                        </Typography>
                      </Box>
                    )}
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{strings("buttons.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlockHistoryModal;
